import router from '@/_router';
import { snackbar } from '@/_core/app-components/use';

import { useCsrfTokenDialogModule } from '@/store/global/appComponents/appCsrfToken';
import { OpenCsrfTokenDialog } from '@/store/global/appComponents/appCsrfToken/actions';
import { bugsnagNotify } from '../utils/bugsnag';
import { RequestDiagnostics } from './requestDiagnostics';

function handleError(error: any, diagnostics?: RequestDiagnostics): void {
    // Payment owners report/present ordinary failures with sanitized context. Keep global
    // authorization and explicit navigation responses on their existing paths below.
    if (diagnostics?.reportingOwner === 'payment' && ![401, 403, 400, 404, 301].includes(error?.response?.status)) return;
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    if (error.response) {
        // Unauthorized
        if (error.response.status == 401) {
            location.href = initAppData.appUrls.loginUrl;
            return;
        }

        // Forbidden
        if (error.response.status == 403) {
            handele403(error.response);
            return;
        }

        // Resource not found
        if (error.response.status == 404) {
            location.href = initAppData.appUrls.pageNotFoundUrl;
            return;
        }

        // Bad request
        if (error.response.status == 400) {
            router.push('/');
            snackbar.showError('Bad request! Something went wrong. You have been redirected to the main screen.');
            return;
        }

        // Internal server error
        if (error.response.status == 500) {
            const ticketId = error.response.headers.ticketid;
            const redirectUrl = initAppData.appUrls.errorPageUrl + (ticketId ? '?ticketid=' + ticketId : '');
            window.location.href = redirectUrl;
            return;
        }

        // Moved - 301 - Reload the app
        if (error.response.status == 301) {
            window.location.href = initAppData.appUrls.appUrl;
            return;
        }

        //Custom code (250) - request processing is ok, but sending teamchat message failed (ettől ne haljon le, de jelezze)
        if (error.response.status == 250) {
            const parsed = JSON.parse(error.response.data);
            snackbar.showError(`Error while sending message to ${parsed.ToolName} application. ${parsed.ToolName} 
            provided error message: ${parsed.ToolProvidedErrorMessage}`);
            return;
        }

        //Others
        if (typeof error.response.data == 'object') {
            snackbar.showError(`Error ${error.response.status} - ${error.response.data.Message}`);
        } else {
            const parsed = JSON.parse(error.response.data);
            let ms = parsed.Message ?? parsed.message;
            ms = ms ?? 'Something went wrong';
            snackbar.showError(`Error ${error.response.status} - ${ms}`);
        }
    }
    // The request was made but no response was received
    // `error.request` is an instance of XMLHttpRequest in the browser and an instance of http.ClientRequest in node.js
    else if (error.request) {
        bugsnagNotify(error);
        if (error.message) {
            snackbar.showError(`Error: ${error.message}`);
        } else {
            snackbar.showError(`Error: ${error.request}`);
        }
    }
    // Something happened in setting up the request that triggered an Error
    else {
        if (error.message != '[Abort by force]') {
            console.log('Error: ', error.message);
            snackbar.showError(`Error ${error.message}`);
        }
    }
}

function handele403(response: any): void {
    const data = JSON.parse(response.data);
    if (data.error && data.error === 'CsrfTokenIsInvalid') {
        const store = useCsrfTokenDialogModule();
        store.dispatch(new OpenCsrfTokenDialog());
    } else if (data.context && data.context == 'DashboardQuery') {
        // Visszavont private link sharingnél a dashboardra dobnánk, de elsőre nem a user workspace-én vagyunk, és megáll a betöltés. Ezzel továbbdobjuk az unauthorized oldalra.
        window.location.reload();
    } else if (data.context === 'StorymapBySlugQuery') {
        router.push('/');
        snackbar.showError("You don't have rights to access that story map. You have been redirected to the main screen.");
    } else {
        if (router.currentRoute.fullPath !== '/') {
            router.push('/');
        }
        snackbar.showError("You don't have the rights to do that. You have been redirected to the main screen.");
    }
}

export { handleError };
